import React from 'react';
import isEmpty from 'lodash/isEmpty';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { ThunkDispatch } from 'redux-thunk';
import { Button } from 'antd';
import { Action } from 'redux';
import {
  IDatesState,
  IRoomsState,
  ILocationsState,
  locationActions,
  datesActions,
  roomsActions,
  getHotels,
  getSuppliers,
  filtersActions,
  IHotelsState,
  IFiltersState,
  hotelsActions,
} from 'src/store/slices/index';
import { RootState } from 'src/store';
import { BlueButton } from 'src/components/components/index';
import {
  DATES_LABEL,
  LOCATION_LABEL,
  LOCATION_OBJECT_LABEL,
  ROOMS_LABEL,
  FILTERS_LABEL,
  SESSION_KEY_LABEL,
} from 'src/constants/index';
import HotelBackground from 'src/assets/images/hotel/bg-hotels.png';
import { SearchSvg, UpdateSvg } from 'src/assets/index';
import { ILocation, IRoom, ISessionKey, IUrlRoom } from 'src/common-types/index';
import { UrlUtils, isDataValid, getNormalizedRooms, getTimeout } from 'src/utils/index';
import { LocationsPicker } from '../locations-picker';
import { RoomPicker } from '../room-picker';
import { DatePicker } from '../date-picker';
import { SearchInfo } from '../search-info';
import './style.scss';

interface IMapStateToProps {
  datesStore: IDatesState;
  roomsStore: IRoomsState;
  locationsStore: ILocationsState;
  hotelsStore: IHotelsState;
  filtersStore: IFiltersState;
}

interface IMapDispatchToProps {
  validateLocation: (error: string) => void;
  validateDates: (error: string) => void;
  getHotels: (
    location: ILocation,
    startDate: string,
    endDate: string,
    rooms: IRoom[],
    isLoadMore: boolean,
    isFiltersChange: boolean,
    isReuseSessionKey: boolean,
  ) => void;
  setDates: (dates: { startDate: string; endDate: string }) => void;
  selectLocation: (location: ILocation) => void;
  setRooms: (rooms: IRoom[]) => void;
  selectLocationLabel: (label: string) => void;
  setFilters: (filters: IFiltersState) => void;
  resetFilters: () => void;
  getSuppliers: () => void;
  setSessionKey: (session: ISessionKey) => void;
}

interface IProps extends IMapDispatchToProps, IMapStateToProps, WrappedComponentProps {}

interface IState {
  isCollapse: boolean;
}

const zero = 0;

class SearchWrapperComponent extends React.Component<IProps, IState> {
  state: IState = {
    isCollapse: false,
  };

  onSearch = (isInitial = false, isReuse = false) => {
    const {
      locationsStore,
      datesStore,
      validateDates,
      validateLocation,
      intl,
      roomsStore,
      getHotels,
      resetFilters,
    } = this.props;
    const { isDatesValid, isLocationValid, isValid, isDatesMaxError } = isDataValid(
      locationsStore,
      datesStore,
    );

    this.setState({ isCollapse: !this.state.isCollapse });

    if (!isLocationValid) {
      validateLocation(intl.formatMessage({ id: 'location.error' }));
    }

    if (!isDatesValid) {
      validateDates(
        intl.formatMessage({ id: isDatesMaxError ? 'maximum.date.range.search' : 'dates.error' }),
      );
    }

    if (isValid) {
      const { selectedLocation } = locationsStore;
      const { startDate, endDate } = datesStore;
      const { rooms } = roomsStore;

      if (!isInitial) {
        resetFilters();
        UrlUtils.setUrl(FILTERS_LABEL, null);
      }

      getHotels(selectedLocation, startDate, endDate, rooms, false, false, isReuse);
    }
  };

  componentDidMount() {
    const {
      setRooms,
      selectLocation,
      setDates,
      selectLocationLabel,
      setFilters,
      getSuppliers,
      setSessionKey,
    } = this.props;
    const values = UrlUtils.getValues();
    let isReuse = false;

    if (values[DATES_LABEL]) {
      setDates(values[DATES_LABEL] as { startDate: string; endDate: string });
    }

    if (values[LOCATION_LABEL]) {
      selectLocationLabel(values[LOCATION_LABEL] as string);
    }

    if (values[LOCATION_OBJECT_LABEL]) {
      selectLocation(values[LOCATION_OBJECT_LABEL] as ILocation);
    }

    if (values[ROOMS_LABEL]) {
      setRooms(getNormalizedRooms(values[ROOMS_LABEL] as Array<IUrlRoom>));
    }

    if (values[FILTERS_LABEL]) {
      setFilters(values[FILTERS_LABEL] as IFiltersState);
    }

    const session: ISessionKey = values[SESSION_KEY_LABEL] as ISessionKey;

    if (session && session.expireDate && getTimeout(session.expireDate) > zero) {
      setSessionKey(session);
      isReuse = true;
    }

    setTimeout(() => {
      const { locationsStore, datesStore } = this.props;
      const { isValid } = isDataValid(locationsStore, datesStore);

      if (isValid) {
        this.onSearch(true, isReuse);
      }
    });

    getSuppliers();
  }

  onToggle = () => {
    this.setState({ isCollapse: !this.state.isCollapse });
  };

  render(): React.ReactNode {
    const { hotelsStore, locationsStore, datesStore } = this.props;
    const { isCollapse } = this.state;
    const { isSearch, hotels } = hotelsStore;
    const values = UrlUtils.getValues();
    const isAfterSearchLoad =
      (isSearch && !(!!locationsStore.error || !!datesStore.error)) || (hotels && !!hotels.length);

    return (
      <div
        className={`search-wrapper ${isAfterSearchLoad && !isEmpty(values) ? 'after-search' : ''} ${
          isCollapse ? 'toggle-search-info' : ''
        }`}
        style={{ backgroundImage: !isAfterSearchLoad && `url(${HotelBackground})` }}
      >
        <div className="search-wrapper__wrapper">
          <h1 className="search-wrapper__header">
            <FormattedMessage id="main.header" />
          </h1>
          <p className="search-wrapper__header-small">
            <FormattedMessage id="main.header.small" />
          </p>
          <div className="search-wrapper__search-section">
            <LocationsPicker />
            <DatePicker />
            <RoomPicker />
            <BlueButton onClick={() => this.onSearch()}>
              {isAfterSearchLoad ? (
                <>
                  <UpdateSvg />
                  <FormattedMessage id="update.search.your.hotel" />
                </>
              ) : (
                <>
                  <SearchSvg />
                  <FormattedMessage id="find.your.hotel" />
                </>
              )}
            </BlueButton>
          </div>
          {isCollapse && <SearchInfo onToggle={this.onToggle} />}
          {isAfterSearchLoad && !isCollapse && (
            <Button
              className="search-wrapper__toggle-search-info"
              onClick={this.onToggle}
              type="text"
            >
              <FormattedMessage id="cancel" />
            </Button>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    datesStore: state.datesStore,
    roomsStore: state.roomsStore,
    locationsStore: state.locationsStore,
    hotelsStore: state.hotelsStore,
    filtersStore: state.filtersStore,
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<RootState, undefined, Action>,
): IMapDispatchToProps => ({
  validateLocation: (error: string) => {
    dispatch(locationActions.setError(error));
  },
  validateDates: (error: string) => {
    dispatch(datesActions.setError(error));
  },
  getHotels: (
    location: ILocation,
    startDate: string,
    endDate: string,
    rooms: IRoom[],
    isLoadMore: boolean,
    isFiltersChange: boolean,
    isReuseSessionKey: boolean,
  ) => {
    dispatch(
      getHotels(
        location,
        startDate,
        endDate,
        rooms,
        isLoadMore,
        isFiltersChange,
        isReuseSessionKey,
      ),
    );
  },
  setDates: (dates: { startDate: string; endDate: string }) => {
    dispatch(datesActions.setDates(dates));
  },
  selectLocation: (location: ILocation) => {
    dispatch(locationActions.selectLocation(location));
  },
  setRooms: (rooms: IRoom[]) => {
    dispatch(roomsActions.setRooms(rooms));
  },
  selectLocationLabel: (label: string) => {
    dispatch(locationActions.selectLocationLabel(label));
  },
  setFilters: (filters: IFiltersState) => {
    dispatch(filtersActions.setFilters(filters));
  },
  setSessionKey: (session: ISessionKey) => {
    dispatch(hotelsActions.setSessionKey(session));
  },
  resetFilters: () => {
    dispatch(filtersActions.resetFilters());
  },
  getSuppliers: () => {
    dispatch(getSuppliers());
  },
});

export const SearchWrapper = connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(SearchWrapperComponent));

